import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as filterActions from '../actions/filter.actions';
import * as documentActions from '../actions/document.actions';
import * as filterSelectors from '../selectors/filter.selector';
import { concatMap, withLatestFrom, map, switchMap, catchError } from 'rxjs/operators';
import { appReducer, documentSelectors } from '@app-root-store';
import { Store, select } from '@ngrx/store';
import { IFilter } from '@app-root-interfaces';
import { DocumentService } from '@app-root-services';
import { of } from 'rxjs';
import { FilterService } from '../../root-services/filter.service';

@Injectable()
export class FilterEffects {
    private actions$ = inject(Actions);
    private store$ = inject<Store<appReducer.AppState>>(Store);
    private documentService = inject(DocumentService);
    private filterService = inject(FilterService);

    changeFilter$ = createEffect(() =>
        this.actions$.pipe(
            ofType(filterActions.changeFilter),
            withLatestFrom(this.store$.pipe(select(filterSelectors.selectFilterFilters))),
            map(([{ payload }, currentFilter]) => {
                return payload.reduce((acc, { key, filter }) => {
                    return {
                        ...acc,
                        [key]: this.addOrRemoveFilter(key, filter, acc)
                    };
                }, currentFilter);
            }),
            withLatestFrom(
                this.store$.pipe(select(filterSelectors.selectFilterSearch)),
                this.store$.pipe(select(filterSelectors.selectFilterSearchAll)),
                this.store$.pipe(select(documentSelectors.selectDocumentAllDriveIds))
            ),
            concatMap(([filters, search, searchAll, driveIds]) =>
                this.documentService.searchFiles(search, filters, searchAll ? null : driveIds).pipe(
                    switchMap((files) => {
                        return of(
                            documentActions.searchFilesSuccess({ files }),
                            filterActions.changeFilterSuccess({ filter: filters })
                        );
                    }),
                    catchError(() =>
                        of(documentActions.searchFilesFailure(), filterActions.changeFilterSuccess({ filter: filters }))
                    )
                )
            )
        )
    );

    getAllFilters$ = createEffect(() =>
        this.actions$.pipe(
            ofType(filterActions.getAllFilters, filterActions.changeFilterSuccess),
            concatMap(() =>
                this.filterService.getAllFilter().pipe(
                    map((filters) => filterActions.getAllFiltersSuccess({ filters })),
                    catchError(() => of(filterActions.getAllFiltersFailure()))
                )
            )
        )
    );

    private addOrRemoveFilter(key: string, filter: string, filters: IFilter): string[] {
        const newFilter = { ...filters, [key]: [...filters[key]] };
        const index = filters[key].indexOf(filter);
        if (index >= 0) {
            newFilter[key].splice(index, 1);
            return newFilter[key];
        }
        return [...newFilter[key], filter];
    }
}
